import { memo, useCallback } from 'react';
import { classnames } from '@/libs/utils';

const sliderStyles = `
  input[type="range"] {
    -webkit-appearance: none;
    width: 100%;
    height: 0.5rem;
    border-radius: 0.5rem;
    background: transparent;
    outline: none;
  }

  input[type="range"]:disabled {
    cursor: not-allowed;
  }
  
  input[type="range"]::-webkit-slider-runnable-track {
    -webkit-appearance: none;
    height: 0.5rem;
    border-radius: 0.5rem;
    background: linear-gradient(
      to right,
      #000 0%,
      #000 calc(((var(--value) - var(--min)) / (var(--max) - var(--min))) * 100%),
      #d1d5db calc(((var(--value) - var(--min)) / (var(--max) - var(--min))) * 100%),
      #d1d5db 100%
    );
  }

  input[type="range"]:disabled::-webkit-slider-runnable-track {
    background: #e5e7eb; /* Tailwind gray-200 */
  }
  
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    background: white;
    cursor: pointer;
    border: 2px solid #000;
    margin-top: -0.375rem;
  }

  input[type="range"]:disabled::-webkit-slider-thumb {
    background: #e5e7eb;      /* gray thumb */
    border-color: #9ca3af;    /* gray-400 border */
    cursor: not-allowed;
  }
  
  input[type="range"]::-moz-range-track {
    height: 0.5rem;
    border-radius: 0.5rem;
    background: linear-gradient(
      to right,
      #000 0%,
      #000 calc(((var(--value) - var(--min)) / (var(--max) - var(--min))) * 100%),
      #d1d5db calc(((var(--value) - var(--min)) / (var(--max) - var(--min))) * 100%),
      #d1d5db 100%
    );
    border: none;
  }

  input[type="range"]:disabled::-moz-range-track {
    background: #e5e7eb;
  }
  
  input[type="range"]::-moz-range-thumb {
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    background: white;
    cursor: pointer;
    border: 2px solid #000;
  }

  input[type="range"]:disabled::-moz-range-thumb {
    background: #e5e7eb;
    border-color: #9ca3af;
    cursor: not-allowed;
  }
`;

interface RangeSliderProps {
  id: string;
  label?: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
  showValue?: boolean;
  testId?: string;
  helperText?: string;
  disabled?: boolean;
}

const RangeSlider = memo(
  ({
    id,
    label,
    min,
    max,
    step,
    value,
    onChange,
    showValue = false,
    testId,
    helperText,
    disabled = false
  }: RangeSliderProps) => {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const numValue = parseFloat(e.target.value);
        if (!isNaN(numValue) && numValue >= min) {
          onChange(numValue);
        }
      },
      [onChange, min]
    );

    return (
      <>
        <style>{sliderStyles}</style>
        <div>
          {label && (
            <label htmlFor={id} className="mb-2 block text-xs font-medium text-gray-700">
              {label}
            </label>
          )}
          <div
            className={classnames('flex items-center gap-3', showValue ? '' : 'w-full', disabled ? 'opacity-50' : '')}
          >
            <input
              id={id}
              data-testid={testId}
              type="range"
              min={min}
              max={max}
              step={step}
              value={value}
              onChange={handleChange}
              style={{ '--value': value, '--min': min, '--max': max } as React.CSSProperties}
              className="h-2 flex-1 cursor-pointer appearance-none disabled:cursor-not-allowed"
              disabled={disabled}
            />
            {showValue && (
              <div className="w-8 rounded bg-gray-100 text-center">
                <span className="text-xs">{Math.round(value)}</span>
                <span className="ml-0.5 text-xs text-gray-400">s</span>
              </div>
            )}
          </div>
          {helperText && <p className="mt-1 text-xs text-gray-500">{helperText}</p>}
        </div>
      </>
    );
  }
);

export default RangeSlider;
